import React from 'react';
import PropTypes from 'prop-types';
import styles from './Calendar.module.scss';
import moment from "moment";
import Day from "./Day/Day";

const isFunction = fn => {
    return fn && {}.toString.call(fn) === '[object Function]';
};

const Calendar = ({children, pivot, start, end, disabled, pastDisabled, todayDisabled, weekStartsOn, selectDayHandler, inlineStyle,
                      availableRange, selectingRange, hoveredDayHandler, filterDaysToDisableHandler, keyAnchor, overrideDayRender, monthOverflowDaysVisible, t}) => {

    const calendarClasses = disabled ? ['calendarPickerFixedClass', styles.calendar, styles.disabled].join(' ') : styles.calendar;

    const unit = 'days';
    const today = moment();
    const weekDays = moment.weekdays();
    const weekStartsOnIdx = weekDays.indexOf(weekStartsOn.charAt(0).toUpperCase() + weekStartsOn.slice(1));
    const weekSortedDays = [...weekDays].splice(weekStartsOnIdx).concat([...weekDays].splice(0, weekStartsOnIdx));
    const monthFirstDay = pivot.clone().startOf('month');
    const monthLastDay = pivot.clone().endOf('month');
    const monthFirstDayIdx = weekSortedDays.indexOf(monthFirstDay.clone().format('dddd'));
    const monthLastDayIdx = weekSortedDays.indexOf(monthLastDay.clone().format('dddd'));
    const calendarFirstDay = monthFirstDay.clone().subtract(monthFirstDayIdx, unit);
    const calendarLastDay = monthLastDay.clone().add(weekSortedDays.length - monthLastDayIdx - 1, unit);

    const weekDaysJSX = weekSortedDays.map(weekDay => (
        <div className={styles.weekDay} key={weekDay} title={t(weekDay)}>{t(weekDay).substring(0, 1)}</div>)
    );

    const days = [];
    for (let d = calendarFirstDay.clone(); d.isBefore(calendarLastDay.clone()); d.add(1, unit)) {
        const current = d.clone();
        const isToday = current.isSame(today, unit);

        const isKeyAnchor = current.isSame(keyAnchor, unit);
        const isOutOfMonthRange = current.isBefore(monthFirstDay) || current.isAfter(monthLastDay);
        const isOutOfAvailableRange = availableRange
            ? current.isBefore(availableRange.start, unit) || current.isAfter(availableRange.end, unit)
            : false;
        const selectingMode = selectingRange
            ? current.isSameOrAfter(selectingRange.start, unit) && current.isSameOrBefore(selectingRange.end, unit)
            : false;
        const isSelected = current.isSameOrAfter(start, unit) && current.isSameOrBefore(end, unit);
        const isDisabled = disabled || isOutOfAvailableRange || (current.isBefore(today, unit) && pastDisabled) || (isToday && todayDisabled) || filterDaysToDisableHandler(current.clone());
        const isWeekend = current.weekday() === 6 || current.weekday() === 0;
        const isToRightSelection =  selectingMode ? current.isSame(selectingRange.start, unit) : current.isSame(start, unit);
        const isToLeftSelection = selectingMode ? current.isSame(selectingRange.end, unit) :  current.isSame(end, unit);

        days.push(
            <Day key={current.format('day-YYYY-MM-DD')}
                 isToday={isToday}
                 selected={isSelected}
                 disabled={isDisabled}
                 isWeekend={isWeekend}
                 isKeyAnchor={isKeyAnchor}
                 selectingMode={selectingMode}
                 isInvisible={!monthOverflowDaysVisible && isOutOfMonthRange}
                 clickHandler={() => selectDayHandler(current.clone())}
                 mouseEnterHandler={() => hoveredDayHandler(current.clone())}
                 //mouseLeaveHandler={() => hoveredDayHandler(start.clone())}
                 toRightSelection={isToRightSelection}
                 toLeftSelection={isToLeftSelection}
                 shy={isOutOfMonthRange}>
                 {isFunction(overrideDayRender) ? overrideDayRender(current) : current.format('D')}
            </Day>
        );
    }

    return (
        <div className={calendarClasses} style={inlineStyle}>
            <div className={styles.weekDaysContainer}>{weekDaysJSX}</div>
            <div className={styles.daysContainer}>{days}</div>
            {children}
        </div>
    );
};

Calendar.propTypes = {
    inlineStyle: PropTypes.object,
    monthOverflowDaysVisible: PropTypes.bool,
    selectDayHandler: PropTypes.func,
    hoveredDayHandler: PropTypes.func,
    filterDaysToDisableHandler: PropTypes.func,
    children: PropTypes.any,
    disabled: PropTypes.bool,
    pastDisabled: PropTypes.bool,
    todayDisabled: PropTypes.bool,
    pivot: PropTypes.instanceOf(moment),
    keyAnchor: PropTypes.instanceOf(moment),
    start: PropTypes.instanceOf(moment),
    end: PropTypes.instanceOf(moment),
    weekStartsOn: PropTypes.oneOf(['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']),
    availableRange: PropTypes.shape({
        start: PropTypes.instanceOf(moment),
        end: PropTypes.instanceOf(moment)
    }),
    selectingRange: PropTypes.shape({
        start: PropTypes.instanceOf(moment),
        end: PropTypes.instanceOf(moment)
    }),
    t: PropTypes.func,
    overrideDayRender: PropTypes.func
};

Calendar.defaultProps = {
    inlineStyle: {},
    monthOverflowDaysVisible: true,
    selectDayHandler: () => null,
    hoveredDayHandler: () => null,
    filterDaysToDisableHandler: () => false,
    children: null,
    disabled: false,
    pastDisabled: false,
    todayDisabled: false,
    pivot: moment(),
    keyAnchor: moment(),
    start: null,
    end: null,
    weekStartsOn: 'Sunday',
    availableRange: null,
    selectingRange: null,
    overrideDayRender: null,
    t: str => str
};

export default Calendar;
